import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Search, Truck, MapPin, Phone, StickyNote } from "lucide-react";

const empty = { category: "item", name: "", quantity: "", quantity_total: 0, quantity_delivered: 0, contact: "", location: "", notes: "" };

function DeliveryLog({ resourceId, onChanged }) {
  const [items, setItems] = useState([]);
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => setItems((await api.get(`/resources/${resourceId}/deliveries`)).data);
  useEffect(() => { if (resourceId) reload(); }, [resourceId]);

  const add = async (e) => {
    e.preventDefault();
    const n = Number(qty);
    if (!n || n <= 0) { toast.error("Jumlah harus > 0"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/resources/${resourceId}/deliveries`, { quantity: n, note });
      setQty(""); setNote("");
      toast.success("Pengiriman dicatat");
      reload();
      onChanged?.(r.data.resource);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Gagal"); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus catatan ini? (jumlah akan dikembalikan)")) return;
    try {
      await api.delete(`/deliveries/${id}`);
      toast.success("Dihapus");
      reload();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-2">
        <Truck className="w-4 h-4" strokeWidth={2} />
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">Log Pengiriman</div>
        {items.length > 0 && <span className="text-[11px] text-[#141414]/50">· {items.length} catatan</span>}
      </div>
      <form onSubmit={add} className="grid grid-cols-12 gap-2 mb-3">
        <input type="number" min="1" value={qty} onChange={(e)=>setQty(e.target.value)} placeholder="Jumlah" data-testid="delivery-qty" className="col-span-4 sm:col-span-3 flat-border rounded-lg px-3 py-2 outline-none text-sm font-mono" />
        <input value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Catatan (opsional)" data-testid="delivery-note" className="col-span-6 sm:col-span-7 flat-border rounded-lg px-3 py-2 outline-none text-sm" />
        <button type="submit" disabled={busy} data-testid="delivery-add" className="col-span-2 flat-btn-primary rounded-lg px-3 py-2 text-sm font-semibold flex items-center justify-center"><Plus className="w-4 h-4" /></button>
      </form>
      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
        {items.length === 0 && <div className="text-xs text-[#141414]/50">Belum ada catatan pengiriman.</div>}
        {items.map((d) => (
          <div key={d.id} data-testid={`delivery-${d.id}`} className="flex items-center gap-3 flat-border rounded-lg px-3 py-1.5 bg-[#fff0d2]/40">
            <span className="font-mono font-bold text-sm shrink-0">+{d.quantity}</span>
            <span className="text-xs flex-1 min-w-0 truncate">{d.note || <span className="text-[#141414]/40">—</span>}</span>
            <span className="text-[10px] text-[#141414]/50 shrink-0 hidden sm:inline">{new Date(d.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}</span>
            <button onClick={()=>remove(d.id)} data-testid={`delivery-del-${d.id}`} className="text-[color:var(--maroon)] hover:text-[color:var(--ink)] p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Resources() {
  const { t, user, settings, lang } = useApp();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const r = await api.get("/resources");
    setItems(r.data);
  };
  useEffect(() => {
    load();
    api.get("/categories/resources").then(r => setCategories(r.data));
  }, []);

  const catLabel = (key) => {
    const c = categories.find(x => x.key === key);
    if (c) return lang === "id" ? c.label_id : c.label_en;
    return t.resources.tabs[key] || key;
  };

  const rows = useMemo(() => {
    let r = tab === "all" ? items : items.filter((i) => i.category === tab);
    if (q.trim()) {
      const s = q.toLowerCase();
      r = r.filter((i) => (`${i.name} ${i.location} ${i.contact} ${i.quantity} ${i.notes}`).toLowerCase().includes(s));
    }
    r = [...r].sort((a, b) => {
      const av = (a[sortKey] || "").toString().toLowerCase();
      const bv = (b[sortKey] || "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return r;
  }, [items, tab, q, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (item) => { setForm(item); setEditing(item.id); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    try {
      const payload = {
        ...form,
        quantity_total: Number(form.quantity_total) || 0,
        quantity_delivered: Number(form.quantity_delivered) || 0,
      };
      if (editing === "new") await api.post("/resources", payload);
      else await api.put(`/resources/${editing}`, payload);
      toast.success("Saved");
      close();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    await api.delete(`/resources/${id}`);
    toast.success("Deleted");
    load();
  };

  const tabs = ["all", ...categories.map(c => c.key)];

  const th = (label, key, testid) => (
    <button onClick={() => toggleSort(key)} data-testid={`th-${testid}`} className="text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-[#141414]/70 hover:text-[#141414] flex items-center gap-1">
      {label}{sortKey === key && <span className="opacity-60">{sortDir === "asc" ? "↑" : "↓"}</span>}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight">{settings?.[`resources_title_${lang}`] || t.resources.title}</h1>
          <p className="mt-2 text-[#141414]/70 max-w-xl text-sm">{settings?.[`resources_subtitle_${lang}`] || t.resources.subtitle}</p>
        </div>
        {user && (
          <button onClick={openNew} data-testid="resource-add-btn" className="flat-btn-primary rounded-full px-5 py-2.5 font-semibold text-sm flex items-center gap-2 self-start">
            <Plus className="w-4 h-4" strokeWidth={2.5} /> {t.resources.add}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-3 mb-4">
        <div className="flat-border rounded-full flex items-center px-4 py-2 gap-2 bg-white w-full">
          <Search className="w-4 h-4 shrink-0" strokeWidth={2} />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="resources-search" placeholder={t.resources.searchPlaceholder} className="flex-1 bg-transparent outline-none text-sm min-w-0" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {tabs.map((k) => (
            <button
              key={k}
              data-testid={`tab-${k}`}
              onClick={() => setTab(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                tab === k ? "bg-[color:var(--maroon)] text-[color:var(--cream)] border-[color:var(--maroon)]" : "bg-transparent border-[color:var(--ink)] text-[color:var(--ink)] hover:bg-[color:var(--ink)] hover:text-[color:var(--cream)]"
              }`}
            >
              {k === "all" ? (t.resources.tabs.all) : catLabel(k)}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile: card layout */}
      <div className="md:hidden space-y-3">
        {rows.length === 0 && (
          <div className="flat-border rounded-2xl bg-white text-center py-10 text-[#141414]/50 text-sm">{t.resources.empty}</div>
        )}
        {rows.map((r) => (
          <div
            key={r.id}
            data-testid={`resource-card-${r.id}`}
            className={`flat-border rounded-2xl bg-white p-4 ${r.low_stock ? "bg-amber-50" : ""}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[15px] leading-tight break-words">{r.name}</div>
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#141414]">
                    {catLabel(r.category)}
                  </span>
                  {r.low_stock && (
                    <span data-testid={`low-stock-mobile-${r.id}`} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500 text-white">
                      Stok Rendah
                    </span>
                  )}
                </div>
              </div>
              {user && (
                <div className="flex items-center shrink-0">
                  <button onClick={() => openEdit(r)} data-testid={`res-edit-mobile-${r.id}`} className="p-1.5 hover:text-[#670003]" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(r.id)} data-testid={`res-delete-mobile-${r.id}`} className="p-1.5 hover:text-[#670003]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>

            {r.quantity_total > 0 ? (
              <div className="mt-2" data-testid={`r-progress-mobile-${r.id}`}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="font-mono text-[13px]">
                    <span className="font-bold">{r.quantity_delivered}</span>
                    <span className="text-[#141414]/50"> / {r.quantity_total}</span>
                  </span>
                  <span className="text-xs font-mono">{Math.round(r.progress_pct || 0)}%</span>
                </div>
                <div className="h-1.5 progress-track">
                  <div className="h-full bg-[color:var(--maroon)]" style={{ width: `${r.progress_pct || 0}%` }} />
                </div>
                <div className="mt-1 text-[10px] text-[#141414]/60">
                  {t.resources.fields.remaining}: <span className="font-mono">{r.quantity_remaining}</span>
                </div>
              </div>
            ) : (
              r.quantity && (
                <div className="mt-1 font-mono text-[13px] text-[#141414]/80">{r.quantity}</div>
              )
            )}

            <div className="mt-3 grid grid-cols-1 gap-1.5 text-[13px]">
              {r.location && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#141414]/60" strokeWidth={2} />
                  <span className="break-words">{r.location}</span>
                </div>
              )}
              {r.contact && (
                <div className="flex items-start gap-2">
                  <Phone className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#141414]/60" strokeWidth={2} />
                  <span className="break-words">{r.contact}</span>
                </div>
              )}
              {r.notes && (
                <div className="flex items-start gap-2">
                  <StickyNote className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#141414]/60" strokeWidth={2} />
                  <span className="break-words text-[#141414]/85">{r.notes}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block flat-border rounded-2xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#141414]/15 bg-[#fff0d2]">
              <th className="p-3">{th(t.resources.fields.name, "name", "r-name")}</th>
              <th className="p-3">{th(t.resources.fields.category, "category", "r-category")}</th>
              <th className="p-3 hidden sm:table-cell">{th(t.resources.fields.quantity_total, "quantity_total", "r-qty")}</th>
              <th className="p-3 hidden md:table-cell">{th(t.resources.fields.progress, "progress_pct", "r-progress")}</th>
              <th className="p-3 hidden md:table-cell">{th(t.resources.fields.location, "location", "r-loc")}</th>
              <th className="p-3 hidden lg:table-cell">{th(t.resources.fields.contact, "contact", "r-contact")}</th>
              <th className="p-3 hidden lg:table-cell"><span className="text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-[#141414]/70">{t.resources.fields.notes}</span></th>
              {user && <th className="p-3"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={user ? 8 : 7} className="text-center py-12 text-[#141414]/50">{t.resources.empty}</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`resource-row-${r.id}`} className={`border-b border-[#141414]/10 hover:bg-[#fff0d2]/50 ${r.low_stock ? "bg-amber-50" : ""}`}>
                <td className="p-3 font-semibold">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{r.name}</span>
                    {r.low_stock && <span data-testid={`low-stock-${r.id}`} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500 text-white">Stok Rendah</span>}
                  </div>
                </td>
                <td className="p-3">
                  <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#141414]">
                    {catLabel(r.category)}
                  </span>
                </td>
                <td className="p-3 hidden sm:table-cell font-mono text-[13px]">
                  {r.quantity_total > 0 ? (
                    <span data-testid={`r-qty-${r.id}`}>
                      <span className="font-bold">{r.quantity_delivered}</span>
                      <span className="text-[#141414]/50"> / {r.quantity_total}</span>
                    </span>
                  ) : (r.quantity || <span className="text-[#141414]/40">—</span>)}
                </td>
                <td className="p-3 hidden md:table-cell">
                  {r.quantity_total > 0 ? (
                    <div className="flex items-center gap-2" data-testid={`r-progress-${r.id}`}>
                      <div className="h-1.5 w-24 progress-track">
                        <div className="h-full bg-[color:var(--maroon)]" style={{ width: `${r.progress_pct || 0}%` }} />
                      </div>
                      <span className="text-xs font-mono w-9 text-right">{Math.round(r.progress_pct || 0)}%</span>
                      <span className="text-[10px] text-[#141414]/60 whitespace-nowrap hidden lg:inline">· {t.resources.fields.remaining} {r.quantity_remaining}</span>
                    </div>
                  ) : <span className="text-[#141414]/40 text-xs">—</span>}
                </td>
                <td className="p-3 hidden md:table-cell text-[13px]">{r.location || "—"}</td>
                <td className="p-3 hidden lg:table-cell text-[13px]">{r.contact || "—"}</td>
                <td className="p-3 hidden lg:table-cell text-[13px] text-[#141414]/85 max-w-[220px] truncate">{r.notes || <span className="text-[#141414]/40">—</span>}</td>
                {user && (
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(r)} data-testid={`res-edit-${r.id}`} className="p-1.5 hover:text-[#670003]" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(r.id)} data-testid={`res-delete-${r.id}`} className="p-1.5 hover:text-[#670003]" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[#141414]/60 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white flat-border-2 rounded-2xl p-6 w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl">{editing === "new" ? t.resources.add : t.admin.edit}</div>
              <button onClick={close} className="flat-btn bg-transparent rounded-full p-1.5"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.resources.fields.category}</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="rfield-category" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none bg-white">
                  {categories.map((c) => <option key={c.key} value={c.key}>{lang === "id" ? c.label_id : c.label_en}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.resources.fields.name}</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rfield-name" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.resources.fields.quantity_total}</label>
                  <input type="number" min="0" value={form.quantity_total} onChange={(e) => setForm({ ...form, quantity_total: e.target.value })} data-testid="rfield-quantity_total" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none font-mono" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.resources.fields.quantity_delivered}</label>
                  <input type="number" min="0" value={form.quantity_delivered} onChange={(e) => setForm({ ...form, quantity_delivered: e.target.value })} data-testid="rfield-quantity_delivered" className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none font-mono" />
                </div>
              </div>
              {["quantity", "contact", "location", "notes"].map((k) => (
                <div key={k}>
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60">{t.resources.fields[k]}</label>
                  <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`rfield-${k}`} className="mt-1 w-full flat-border rounded-lg px-3 py-2 outline-none" placeholder={k === "quantity" ? "misal: Rp 145.000.000 (opsional)" : ""} />
                </div>
              ))}
            </div>
            {editing !== "new" && editing && (
              <div className="mt-5 pt-4 border-t border-[#141414]/15">
                <DeliveryLog resourceId={editing} onChanged={(updated) => { load(); if (updated) setForm((f) => ({ ...f, quantity_delivered: updated.quantity_delivered })); }} />
              </div>
            )}
            <div className="flex gap-2 mt-6">
              <button onClick={save} data-testid="res-save-btn" className="flat-btn-primary rounded-full px-5 py-2.5 font-semibold text-sm flex items-center gap-2"><Save className="w-4 h-4" /> {t.admin.save}</button>
              <button onClick={close} className="flat-btn bg-transparent rounded-full px-5 py-2.5 font-semibold text-sm">{t.admin.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
